import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getVehicleBrand } from '../../utils/vehicleBrand';
import {
  buildAgendaQuery,
  getAgendaCalendarRange,
} from '../../utils/agendaDateRange';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useBreakpointValue,
  Text,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Button,
  Box,
  Grid,
  GridItem,
  Flex,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
} from '@chakra-ui/react';
import AvailableTimes from '../../components/Appointment/AvailableTimes';
import { getData, postData, putData } from '../../utils/httpClient';
import {
  WARRANTY_TYPES,
  DEFAULT_WARRANTY_TYPE_CODE,
  WARRANTY_STATUSES,
  DEFAULT_WARRANTY_STATUS,
  resolveWarrantyExpiration,
} from '../../utils/warrantyTypes';
import WarrantyDocumentsPanel from './WarrantyDocumentsPanel';

const AGENDA_STATUS_FALLBACK = [
  'Pendiente',
  'Confirmado',
  'Recepcionado',
  'En curso',
  'Completado',
  'Finalizado',
  'Cancelado',
];

const RECEPTION_AVAILABLE_STATUSES = [
  'Recepcionado',
  'En curso',
  'Completado',
  'Finalizado',
];

const FINISHED_SERVICE_STATUSES = ['Completado', 'Finalizado'];

const EMPTY_PDF_DOCS = {
  receptionDocumentId: null,
  serviceDocumentId: null,
  warrantyDocumentId: null,
};

const getRepairVehicleServiceId = (service) => {
  const repairAgenda =
    service?.warranty?.repairAgenda ||
    service?.warranties?.repairAgenda ||
    null;
  return (
    repairAgenda?.vehicleServiceId ||
    repairAgenda?.vehicleService?.id ||
    null
  );
};

const resolveServiceAgendaStatus = (service) => {
  if (!service) return '';
  const agendaEntry = Array.isArray(service.agenda)
    ? service.agenda.find((item) => item?.status) || service.agenda[0]
    : service.agenda;
  return String(service?.status?.name || agendaEntry?.status || '').trim();
};

const resolveRepairAgendaStatus = (repairAgenda) => {
  if (!repairAgenda) return '';
  return String(
    repairAgenda?.vehicleService?.status?.name || repairAgenda?.status || '',
  ).trim();
};

const parseAgendaResponse = (response) => {
  if (!response) return null;
  if (response?.payload?.agenda) return response.payload.agenda;
  if (response?.id) return response;
  return null;
};

const withStatusOption = (options, status) => {
  if (!status) return options;
  return options.includes(status) ? options : [status, ...options];
};

const WarrantyEditModal = ({
  modalState,
  closeModal,
  selectedService,
  onSave,
}) => {
  const modalWidth = useBreakpointValue({ base: '90%', md: '80%' });
  const toast = useToast();

  const [editedService, setEditedService] = useState(selectedService);
  const [vehicleRepairDate, setVehicleRepairDate] = useState('');
  const [vehicleRepairTime, setVehicleRepairTime] = useState('');
  const [repairAgendaStatus, setRepairAgendaStatus] = useState('');
  const [serviceAgendaStatus, setServiceAgendaStatus] = useState('');
  const [statusList, setStatusList] = useState([]);
  const [agendaStatusOptions, setAgendaStatusOptions] = useState(
    AGENDA_STATUS_FALLBACK,
  );
  const [warrantyPdfDocs, setWarrantyPdfDocs] = useState(EMPTY_PDF_DOCS);
  const [repairPdfDocs, setRepairPdfDocs] = useState(EMPTY_PDF_DOCS);
  const [printingPdfKey, setPrintingPdfKey] = useState(null);
  const [associatingLastReception, setAssociatingLastReception] = useState(false);

  const [showRepairTime, setShowRepairTime] = useState(false);
  const [recommendTick, setRecommendTick] = useState(0);
  const [cancellingRepair, setCancellingRepair] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(60); // Duración por defecto en minutos
  
  // Estados para el calendario
  const [appointmentList, setAppointmentList] = useState([]);
  const [holidayList, setHolidayList] = useState([]);
  const [, setMechanicsList] = useState([]);
  const [selectedMechanicId, setSelectedMechanicId] = useState(null);
  const [, setSelectedDate] = useState('');

  // Nuevo servicio para la reparación
  const [newRepairService, setNewRepairService] = useState({
    mechanicId: null,
    serviceTypeId: 3, // Tipo de servicio reparación
    serviceId: null,
    customerTransferId: 1, // Por defecto
    customerId: null,
    vehicleId: null,
    estimatedTime: 60,
    date: '',
    time: '',
    observations: 'Reparación de garantía',
    customerObservations: '',
    status: 'Pendiente',
  });

  useEffect(() => {
    let cancelled = false;

    const initFromService = async () => {
      if (!selectedService) return;

      const existingWarrantyFromService =
        selectedService?.warranties || selectedService?.warranty || null;
      let existingWarranty = existingWarrantyFromService;

      if (existingWarranty?.id) {
        try {
          const warrantyResponse = await getData(
            `/warranty/${existingWarranty.id}`,
            { skipLoader: true },
          );
          if (warrantyResponse?.payload?.warranty) {
            existingWarranty = warrantyResponse.payload.warranty;
          }
        } catch (warrantyError) {
          console.error('Error cargando garantía completa:', warrantyError);
        }
      }

      if (cancelled) return;

      const serviceStatus = resolveServiceAgendaStatus(selectedService);
      let repairAgenda = existingWarranty?.repairAgenda || null;
      const repairAgendaId =
        existingWarranty?.repairAgendaId || repairAgenda?.id || null;

      if (repairAgendaId) {
        const needsFetch =
          !repairAgenda ||
          !resolveRepairAgendaStatus(repairAgenda) ||
          !repairAgenda?.date ||
          !repairAgenda?.time;

        if (needsFetch) {
          try {
            const response = await getData(`/agenda/${repairAgendaId}`, {
              skipLoader: true,
            });
            const fetchedAgenda = parseAgendaResponse(response);
            if (fetchedAgenda) {
              repairAgenda = { ...repairAgenda, ...fetchedAgenda };
            }

            if (
              repairAgenda?.vehicleServiceId &&
              !repairAgenda?.vehicleService?.status?.name
            ) {
              try {
                const vsResponse = await getData(
                  `/vehicleServices/${repairAgenda.vehicleServiceId}`,
                  { skipLoader: true },
                );
                const vs = vsResponse?.payload?.vehicleService;
                if (vs) {
                  repairAgenda = {
                    ...repairAgenda,
                    vehicleService: {
                      ...(repairAgenda.vehicleService || {}),
                      ...vs,
                      status: vs.status,
                    },
                  };
                }
              } catch (vsError) {
                console.error('Error cargando servicio de reparación:', vsError);
              }
            }
          } catch (error) {
            console.error('Error cargando agenda de reparación:', error);
          }
        }
      }

      const repairStatus = resolveRepairAgendaStatus(repairAgenda);
      const warrantyExpiration = resolveWarrantyExpiration(
        existingWarranty,
        selectedService,
      );
      const warrantyState = {
        ...existingWarranty,
        warrantyType:
          existingWarranty?.warrantyType ||
          selectedService?.warrantyType ||
          DEFAULT_WARRANTY_TYPE_CODE,
        status: existingWarranty?.status || DEFAULT_WARRANTY_STATUS,
        warrantyExpiration: warrantyExpiration || null,
        repairAgendaId,
        repairAgenda,
      };
      const normalizedService = {
        ...selectedService,
        warranty: warrantyState,
        warranties: warrantyState,
      };

      setEditedService(normalizedService);
      setServiceAgendaStatus(serviceStatus);
      setRepairAgendaStatus(repairStatus);
      setAgendaStatusOptions((prev) =>
        withStatusOption(withStatusOption(prev, serviceStatus), repairStatus),
      );

      const repairAgendaStatusName = String(repairAgenda?.status || repairStatus || '')
        .toLowerCase();
      const repairIsScheduled =
        Boolean(repairAgenda?.date && repairAgenda?.time) &&
        repairAgendaStatusName !== 'cancelado';

      if (repairIsScheduled) {
        const repairDate = String(repairAgenda.date).slice(0, 10);
        const repairTime = String(repairAgenda.time).slice(0, 5);
        setVehicleRepairDate(repairDate);
        setVehicleRepairTime(repairTime);
        const mins = Number(repairAgenda.estimatedTime) || 60;
        setEstimatedTime(mins);
        setNewRepairService((prev) => ({
          ...prev,
          date: repairDate,
          time: repairTime,
          estimatedTime: mins,
          mechanicId:
            repairAgenda.mechanicId ||
            selectedService?.mechanic?.id ||
            selectedService?.mechanicId ||
            null,
          customerId: selectedService?.customerId || null,
          vehicleId: selectedService?.vehicleId || null,
          status: repairStatus || 'Pendiente',
          description: 'Reparación de garantía',
          customerObservations:
            repairAgenda?.vehicleService?.customerObservations ||
            selectedService?.customerObservations ||
            '',
        }));
        setShowRepairTime(false);
      } else {
        setVehicleRepairDate('');
        setVehicleRepairTime('');
        setNewRepairService((prev) => ({
          ...prev,
          customerObservations:
            prev.customerObservations ||
            selectedService?.customerObservations ||
            '',
        }));
      }

      if (selectedService?.mechanic?.id || selectedService?.mechanicId) {
        const mechanicId =
          selectedService?.mechanic?.id || selectedService?.mechanicId;
        setSelectedMechanicId(mechanicId);
        setNewRepairService((prev) => ({
          ...prev,
          mechanicId: prev.mechanicId || mechanicId,
          customerId: selectedService?.customerId || null,
          vehicleId: selectedService?.vehicleId || null,
        }));
      } else {
        setNewRepairService((prev) => ({
          ...prev,
          customerId: selectedService?.customerId || null,
          vehicleId: selectedService?.vehicleId || null,
        }));
      }
    };

    initFromService();

    return () => {
      cancelled = true;
    };
  }, [selectedService]);

  useEffect(() => {
    if (serviceAgendaStatus) {
      setAgendaStatusOptions((prev) =>
        withStatusOption(prev, serviceAgendaStatus),
      );
    }
  }, [serviceAgendaStatus]);

  useEffect(() => {
    if (repairAgendaStatus) {
      setAgendaStatusOptions((prev) =>
        withStatusOption(prev, repairAgendaStatus),
      );
    }
  }, [repairAgendaStatus]);

  const warrantyServiceId = editedService?.id || selectedService?.id || null;
  const repairVehicleServiceId = useMemo(
    () => getRepairVehicleServiceId(editedService || selectedService),
    [editedService, selectedService],
  );

  const loadPdfAvailability = useCallback(async () => {
    if (!warrantyServiceId) {
      setWarrantyPdfDocs(EMPTY_PDF_DOCS);
      setRepairPdfDocs(EMPTY_PDF_DOCS);
      return;
    }

    try {
      const warrantyRes = await getData(
        `/vehicleServices/${warrantyServiceId}/pdf-documents`,
        { skipLoader: true },
      );
      setWarrantyPdfDocs({
        receptionDocumentId: warrantyRes?.payload?.receptionDocumentId || null,
        serviceDocumentId: warrantyRes?.payload?.serviceDocumentId || null,
        warrantyDocumentId: warrantyRes?.payload?.warrantyDocumentId || null,
      });
    } catch {
      setWarrantyPdfDocs(EMPTY_PDF_DOCS);
    }

    if (!repairVehicleServiceId) {
      setRepairPdfDocs(EMPTY_PDF_DOCS);
      return;
    }

    try {
      const repairRes = await getData(
        `/vehicleServices/${repairVehicleServiceId}/pdf-documents`,
        { skipLoader: true },
      );
      setRepairPdfDocs({
        receptionDocumentId: repairRes?.payload?.receptionDocumentId || null,
        serviceDocumentId: repairRes?.payload?.serviceDocumentId || null,
        warrantyDocumentId: repairRes?.payload?.warrantyDocumentId || null,
      });
    } catch {
      setRepairPdfDocs(EMPTY_PDF_DOCS);
    }
  }, [warrantyServiceId, repairVehicleServiceId]);

  useEffect(() => {
    if (!modalState.isOpen) return;
    loadPdfAvailability();
  }, [
    modalState.isOpen,
    loadPdfAvailability,
    serviceAgendaStatus,
    repairAgendaStatus,
    editedService?.warranty?.status,
  ]);

  const mainAgendaStatus =
    serviceAgendaStatus ||
    resolveServiceAgendaStatus(editedService || selectedService);
  const repairStatus =
    repairAgendaStatus ||
    resolveRepairAgendaStatus(
      editedService?.warranty?.repairAgenda ||
        selectedService?.warranties?.repairAgenda ||
        null,
    );
  const warrantyAdminStatus =
    editedService?.warranty?.status ||
    editedService?.warranties?.status ||
    selectedService?.warranties?.status ||
    '';

  const canPrintReception =
    RECEPTION_AVAILABLE_STATUSES.includes(mainAgendaStatus) ||
    Boolean(warrantyPdfDocs.receptionDocumentId);

  const canPrintWarrantyServiceReport =
    Boolean(warrantyServiceId) &&
    (FINISHED_SERVICE_STATUSES.includes(mainAgendaStatus) ||
      Boolean(warrantyPdfDocs.serviceDocumentId));

  const canPrintWarrantyFinal =
    warrantyAdminStatus === 'FINALIZADA' ||
    FINISHED_SERVICE_STATUSES.includes(mainAgendaStatus) ||
    Boolean(warrantyPdfDocs.warrantyDocumentId);

  const canPrintRepairReception =
    Boolean(repairVehicleServiceId) &&
    (RECEPTION_AVAILABLE_STATUSES.includes(repairStatus) ||
      Boolean(repairPdfDocs.receptionDocumentId));

  const canPrintRepairServiceReport =
    Boolean(repairVehicleServiceId) &&
    (FINISHED_SERVICE_STATUSES.includes(repairStatus) ||
      Boolean(repairPdfDocs.serviceDocumentId));

  const disabledReasons = {
    warrantyReception: canPrintReception
      ? ''
      : 'Disponible cuando la garantía fue recepcionada en agenda.',
    warrantyService: canPrintWarrantyServiceReport
      ? ''
      : 'Disponible cuando el diagnóstico está Completado o Finalizado.',
    warrantyFinal: canPrintWarrantyFinal
      ? ''
      : 'Disponible cuando la garantía está finalizada o el diagnóstico completado.',
    repairReception: canPrintRepairReception
      ? ''
      : !repairVehicleServiceId
        ? 'Agendá la reparación para habilitar la recepción.'
        : 'Disponible cuando la reparación fue recepcionada.',
    repairService: canPrintRepairServiceReport
      ? ''
      : !repairVehicleServiceId
        ? 'Agendá la reparación para habilitar el informe.'
        : 'Disponible cuando la reparación está Completada o Finalizada.',
  };

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const response = await getData('/status', { skipLoader: true });
        const list = response?.payload?.status || [];
        setStatusList(list);
        const names = list
          .map((s) => s?.name)
          .filter(Boolean);
        if (names.length) {
          setAgendaStatusOptions((prev) => {
            const merged = [...names];
            prev.forEach((name) => {
              if (!merged.includes(name)) merged.push(name);
            });
            if (repairAgendaStatus && !merged.includes(repairAgendaStatus)) {
              merged.unshift(repairAgendaStatus);
            }
            return merged;
          });
        }
      } catch {
        setAgendaStatusOptions((prev) =>
          withStatusOption(AGENDA_STATUS_FALLBACK, repairAgendaStatus),
        );
      }
    };
    loadStatuses();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Funciones para obtener datos del API
  const getAppointments = async (anchorDate) => {
    try {
      const { from, to } = getAgendaCalendarRange(anchorDate || new Date());
      const response = await getData(`/agenda${buildAgendaQuery(from, to)}`);
      setAppointmentList(response?.payload?.agendas || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const getHolidays = async () => {
    try {
      const response = await getData('/holidays');
      setHolidayList(response?.payload?.holidays || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const getMechanics = async () => {
    try {
      const response = await getData('/users/mechanics');
      setMechanicsList(response?.payload?.mechanics || []);
    } catch (error) {
      console.error('Error fetching mechanics:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'customerObservations' || name === 'mechanicObservation') {
      setEditedService((prev) => ({
        ...prev,
        [name]: value,
      }));
      return;
    }
    setEditedService((prev) => {
      const nextWarranty = {
        ...(prev.warranty || prev.warranties || {}),
        [name]: value,
      };
      return {
        ...prev,
        warranty: nextWarranty,
        warranties: nextWarranty,
      };
    });
  };

  const handleAssociateLastReception = async () => {
    const serviceId = editedService?.id || selectedService?.id;
    if (!serviceId) {
      toast({
        title: 'Servicio no disponible',
        description: 'No se encontró el servicio de garantía.',
        status: 'warning',
        duration: 3500,
        isClosable: true,
      });
      return;
    }
    setAssociatingLastReception(true);
    try {
      const response = await postData(
        `/vehicleServices/${serviceId}/associate-last-reception`,
        {},
      );
      const updated = response?.payload?.vehicleService;
      if (!updated) {
        throw new Error('No se obtuvo el servicio actualizado.');
      }
      setEditedService((prev) => ({
        ...prev,
        // Conservar petición del cliente; solo datos de recepción.
        customerObservations: prev?.customerObservations,
        date: updated.date ?? prev?.date,
        serviceTypeId: updated.serviceTypeId ?? prev?.serviceTypeId,
        serviceType: updated.serviceType || prev?.serviceType,
        warrantyType: updated.warrantyType ?? prev?.warrantyType,
        creatorUserId: updated.creatorUserId ?? prev?.creatorUserId,
        receptionAt: updated.receptionAt ?? prev?.receptionAt,
        odometer: updated.odometer ?? prev?.odometer,
        status: updated.status || prev?.status,
        statusId: updated.statusId ?? prev?.statusId,
        warranty: {
          ...(prev?.warranty || {}),
          warrantyType:
            updated.warrantyType ??
            prev?.warranty?.warrantyType ??
            prev?.warrantyType,
        },
        vehicle: {
          ...(prev?.vehicle || {}),
          ...(updated.vehicle || {}),
          ...(updated.odometer != null
            ? { kilometers: updated.odometer }
            : {}),
        },
      }));
      if (updated.status?.name) {
        setServiceAgendaStatus(updated.status.name);
      }
      toast({
        title: 'Recepción asociada',
        description:
          'Servicio actualizado a Recepcionado sin modificar la petición del cliente.',
        status: 'success',
        duration: 3500,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'No se pudo asociar la recepción',
        description:
          err?.message ||
          'No hay una recepción previa para este vehículo.',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setAssociatingLastReception(false);
    }
  };

  const handleAddPiece = () => {
    setEditedService((prev) => ({
      ...prev,
      warranty: {
        ...prev.warranty,
        warrantyProducts: [
          ...(prev.warranty?.warrantyProducts || []),
          {
            id: null,
            productDescription: '',
            productNumber: '',
            quantity: 0,
            operationNumber: '',
            description: '',
            timeUnit: '',
          },
        ],
      },
    }));
  };

  const handlePieceChange = (index, e) => {
    const { name, value } = e.target;

    setEditedService((prev) => {
      const updatedWarrantyProducts = [
        ...(prev.warranty?.warrantyProducts || []),
      ];

      updatedWarrantyProducts[index] = {
        ...updatedWarrantyProducts[index],
        [name]: name === 'quantity' ? parseInt(value, 10) || 0 : value,
      };

      return {
        ...prev,
        warranty: {
          ...prev.warranty,
          warrantyProducts: updatedWarrantyProducts,
        },
      };
    });
  };

  const handleAppointmentRangeChange = async (from, to) => {
    try {
      const response = await getData(`/agenda${buildAgendaQuery(from, to)}`);
      setAppointmentList(response?.payload?.agendas || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const handleShowRepairTime = async () => {
    await Promise.all([
      getAppointments(new Date()),
      getHolidays(),
      getMechanics(),
    ]);
    setShowRepairTime(true);
  };

  useEffect(() => {
    if (!showRepairTime && !(vehicleRepairDate && vehicleRepairTime)) return;
    getHolidays();
    getMechanics();
  }, [showRepairTime, vehicleRepairDate, vehicleRepairTime]);

  const handleSearchRecommendedSchedule = () => {
    if (Number(estimatedTime) <= 0) {
      toast({
        title: 'Duración requerida',
        description:
          'Ingresá el tiempo estimado antes de buscar el horario recomendado.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setNewRepairService((prev) => ({
      ...prev,
      estimatedTime: Number(estimatedTime) || prev.estimatedTime,
    }));
    setRecommendTick((tick) => tick + 1);
  };

  const handleConfirmManualRepairSchedule = () => {
    const date = newRepairService.date || vehicleRepairDate;
    const time = newRepairService.time || vehicleRepairTime;
    const duration = Number(estimatedTime) || 0;

    if (!date || !time) {
      toast({
        title: 'Horario requerido',
        description: 'Seleccioná un horario en el calendario.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (duration <= 0) {
      toast({
        title: 'Duración requerida',
        description: 'Ingresá el tiempo estimado en minutos.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setVehicleRepairDate(date);
    setVehicleRepairTime(time);
    setNewRepairService((prev) => ({
      ...prev,
      date,
      time,
      estimatedTime: duration,
      customerId: selectedService?.customerId || prev.customerId,
      vehicleId: selectedService?.vehicleId || prev.vehicleId,
      mechanicId:
        selectedMechanicId ||
        selectedService?.mechanicId ||
        prev.mechanicId,
    }));
    setShowRepairTime(false);
    toast({
      title: 'Reparación programada',
      description: `Se ha programado la reparación para el ${date} a las ${time} (${duration} min).`,
      status: 'success',
      duration: 4000,
      isClosable: true,
    });
  };

  const handleRepairServiceChange = (key, value) => {
    // Solo actualizar los valores en el estado temporal
    setNewRepairService((prev) => ({
      ...prev,
      [key]: value,
    }));

    if (key === 'date' && value) {
      setSelectedDate(value);
    }
  };

  const handleResetRepair = () => {
    setVehicleRepairDate('');
    setVehicleRepairTime('');
    setShowRepairTime(false);
    setEstimatedTime(60);
    setNewRepairService({
      mechanicId: selectedService?.mechanic?.id || selectedService?.mechanicId || null,
      serviceTypeId: 3,
      serviceId: null,
      customerTransferId: 1,
      customerId: selectedService?.customerId || null,
      vehicleId: selectedService?.vehicleId || null,
      estimatedTime: 60,
      date: '',
      time: '',
      observations: 'Reparación de garantía',
      customerObservations: selectedService?.customerObservations || '',
      status: 'Pendiente',
      description: 'Reparación de garantía',
    });
  };

  const handleCancelRepairAppointment = async () => {
    const warranty = editedService?.warranty || editedService?.warranties || null;
    const repairAgendaId =
      warranty?.repairAgendaId || warranty?.repairAgenda?.id || null;

    if (!repairAgendaId) {
      handleResetRepair();
      setRepairAgendaStatus('');
      return;
    }

    setCancellingRepair(true);
    try {
      const currentAgenda =
        parseAgendaResponse(await getData(`/agenda/${repairAgendaId}`)) ||
        warranty?.repairAgenda;
      if (currentAgenda?.id) {
        const {
          customer,
          vehicle,
          service,
          mechanic,
          serviceType,
          customerTransfer,
          vehicleService,
          ...agendaPayload
        } = currentAgenda;
        await putData(`/agenda/${currentAgenda.id}`, {
          ...agendaPayload,
          status: 'Cancelado',
        });
        const cancelStatus = statusList.find((item) => item.name === 'Cancelado');
        if (currentAgenda.vehicleServiceId && cancelStatus?.id) {
          await putData(`/vehicleServices/${currentAgenda.vehicleServiceId}`, {
            id: currentAgenda.vehicleServiceId,
            statusId: cancelStatus.id,
          });
        }
      }

      const nextStatus =
        warranty?.status === 'REP. AGENDADA' ? 'PENDIENTE' : warranty?.status;
      if (warranty?.id) {
        await putData(`/warranty/${warranty.id}`, {
          id: warranty.id,
          customerId: warranty.customerId || editedService.customerId,
          vehicleId: warranty.vehicleId || editedService.vehicleId,
          serviceId: warranty.serviceId || editedService.id,
          warrantyType: warranty.warrantyType || DEFAULT_WARRANTY_TYPE_CODE,
          status: nextStatus || DEFAULT_WARRANTY_STATUS,
          repairAgendaId: null,
          date: warranty.date || null,
          warrantyExpiration: warranty.warrantyExpiration || null,
          stoppedVehicle: warranty.stoppedVehicle,
          atApPosition: warranty.atApPosition || null,
          faultCode: warranty.faultCode || null,
          manufacturerCode: warranty.manufacturerCode || null,
          authorizationRequestSent: warranty.authorizationRequestSent || null,
          claimSent: warranty.claimSent || null,
          diss: warranty.diss || null,
          tpi: warranty.tpi || null,
          saga: warranty.saga || null,
          claim: warranty.claim || null,
          authorization: warranty.authorization || null,
          warrantyProducts: warranty.warrantyProducts || [],
          customerObservations: editedService.customerObservations ?? null,
          mechanicObservation: editedService.mechanicObservation ?? null,
        });
      }

      setEditedService((prev) => {
        const current = prev?.warranty || prev?.warranties || {};
        const cleared = {
          ...current,
          repairAgendaId: null,
          repairAgenda: null,
          status: nextStatus || current.status,
        };
        return {
          ...prev,
          warranty: cleared,
          warranties: prev?.warranties ? cleared : prev?.warranties,
        };
      });
      setRepairAgendaStatus('');
      handleResetRepair();
      toast({
        title: 'Cita cancelada',
        description: 'La reparación quedó cancelada.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'No se pudo cancelar la cita',
        description: error?.message || 'Intentá nuevamente.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setCancellingRepair(false);
    }
  };

  const openPdfByDocumentId = async (documentId, label = 'PDF') => {
    if (!documentId) return;
    try {
      const response = await getData(`/document/${documentId}`, {
        skipLoader: true,
      });
      const document = response?.payload?.document;
      if (!document?.file) {
        throw new Error('Documento vacío');
      }
      const pdfData = new Uint8Array(document.file.data);
      const blob = new Blob([pdfData], { type: 'application/pdf' });
      window.open(URL.createObjectURL(blob), '_blank');
    } catch (err) {
      toast({
        title: `${label} no disponible`,
        description: err?.message || 'No se pudo abrir el documento.',
        status: 'warning',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const regenerateAndOpenPdf = async (serviceId, kind, label, updateDocs) => {
    if (!serviceId) return;
    setPrintingPdfKey(`${kind}-${serviceId}`);
    try {
      const endpoint =
        kind === 'reception'
          ? `/vehicleServices/${serviceId}/reception-pdf`
          : kind === 'warranty'
            ? `/vehicleServices/${serviceId}/warranty-pdf`
            : `/vehicleServices/${serviceId}/service-pdf`;
      const response = await postData(endpoint, {}, { skipLoader: true });
      const docId =
        response?.payload?.documentId ||
        response?.payload?.vehicleService?.documentId;
      if (!docId) {
        throw new Error('No se obtuvo el documento generado.');
      }
      if (updateDocs) updateDocs(docId);
      await openPdfByDocumentId(docId, label);
    } catch (err) {
      toast({
        title: `No se pudo imprimir ${label.toLowerCase()}`,
        description:
          err?.response?.data?.message ||
          err?.message ||
          'Intentá nuevamente.',
        status: 'error',
        duration: 4500,
        isClosable: true,
      });
    } finally {
      setPrintingPdfKey(null);
    }
  };

  const hasRepairScheduled = Boolean(
    editedService?.warranty?.repairAgendaId ||
      (vehicleRepairDate && vehicleRepairTime),
  );

  return (
    <Modal isOpen={modalState.isOpen} onClose={closeModal} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW={modalWidth} borderRadius="xl">
        <ModalCloseButton />
        <ModalBody p={6}>
          {/* Título del Modal */}
          <Text fontSize="2xl" fontWeight="bold" color="teal.500" mb={4}>
            Editar Garantía
          </Text>

          {/* Primera área: Datos del vehículo, mecánico y cliente */}
          <Box
            mb={6}
            borderWidth="1px"
            borderRadius="xl"
            boxShadow="md"
            bg="white"
          >
            <Box p={6}>
              <Text fontWeight="bold" fontSize="xl" color="gray.700" mb={5}>
                Información del Vehículo, Mecánico y Cliente
              </Text>
              <Grid
                templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }}
                gap={6}
              >
                <GridItem>
                  <Text fontWeight="semibold" color="gray.600" mb={1}>
                    Marca:
                  </Text>
                  <Text mb={3}>{getVehicleBrand(editedService?.vehicle) || '-'}</Text>

                  <Text fontWeight="semibold" color="gray.600" mb={1}>
                    Modelo:
                  </Text>
                  <Text mb={3}>{editedService?.vehicle?.model?.description || editedService?.vehicle?.model || '-'}</Text>

                  <Text fontWeight="semibold" color="gray.600" mb={1}>
                    Año:
                  </Text>
                  <Text mb={3}>{editedService?.vehicle?.year || '-'}</Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="semibold" color="gray.600" mb={1}>
                    Matrícula:
                  </Text>
                  <Text mb={3}>{editedService?.vehicle?.plate || '-'}</Text>

                  <Text fontWeight="semibold" color="gray.600" mb={1}>
                    Chasis:
                  </Text>
                  <Text mb={3}>
                    {editedService?.vehicle?.chassisNumber || '-'}
                  </Text>

                  <Text fontWeight="semibold" color="gray.600" mb={1}>
                    Mecánico:
                  </Text>
                  <Text mb={3}>{editedService?.mechanic?.name || '-'}</Text>
                </GridItem>

                <GridItem>
                  <Text fontWeight="semibold" color="gray.600" mb={1}>
                    Cliente:
                  </Text>
                  <Text mb={3}>{editedService?.customer?.name || '-'}</Text>

                  <Text fontWeight="semibold" color="gray.600" mb={1}>
                    Teléfono:
                  </Text>
                  <Text mb={3}>{editedService?.customer?.phone || '-'}</Text>

                  <Text fontWeight="semibold" color="gray.600" mb={1}>
                    Correo:
                  </Text>
                  <Text mb={3}>{editedService?.customer?.email || '-'}</Text>
                </GridItem>
              </Grid>
            </Box>
          </Box>

          <Tabs mt={2} variant="enclosed" colorScheme="teal">
            <TabList>
              <Tab>
                Garantía (Diagnóstico)
                {mainAgendaStatus && (
                  <Badge ml={2} colorScheme="teal" fontSize="xs">
                    {mainAgendaStatus}
                  </Badge>
                )}
              </Tab>
              <Tab>
                Reparación
                <Badge ml={2} colorScheme="blue" fontSize="xs">
                  {hasRepairScheduled ? repairStatus || 'Agendada' : 'Sin agendar'}
                </Badge>
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel px={0} pt={4}>
          {/* Información de la garantía */}
          <Box
            mb={6}
          >
            <Box p={6}>
              <FormControl maxW={{ base: '100%', md: '320px' }} mb={5}>
                <FormLabel fontWeight="semibold" color="gray.600" mb={1}>
                  Estado en agenda (diagnóstico)
                </FormLabel>
                <Select
                  size="sm"
                  bg="white"
                  value={serviceAgendaStatus}
                  onChange={(e) => setServiceAgendaStatus(e.target.value)}
                >
                  {!serviceAgendaStatus && (
                    <option value="" disabled>
                      Seleccionar estado
                    </option>
                  )}
                  {agendaStatusOptions.map((status) => (
                    <option key={`svc-${status}`} value={status}>
                      {status}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Text fontWeight="bold" fontSize="xl" color="gray.700" mb={5}>
                Información de la Garantía
              </Text>
              <Grid
                templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }}
                gap={6}
              >
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Tipo de garantía:
                  </Text>
                  <Select
                    value={
                      editedService?.warranty?.warrantyType ||
                      DEFAULT_WARRANTY_TYPE_CODE
                    }
                    onChange={handleChange}
                    name="warrantyType"
                  >
                    {WARRANTY_TYPES.map((t) => (
                      <option key={t.code} value={t.code}>
                        {t.label}
                      </option>
                    ))}
                  </Select>
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Estado garantía:
                  </Text>
                  <Select
                    value={
                      editedService?.warranty?.status || DEFAULT_WARRANTY_STATUS
                    }
                    onChange={handleChange}
                    name="status"
                  >
                    {WARRANTY_STATUSES.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </Select>
                </GridItem>
                {/* Primera fila */}
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Fecha:
                  </Text>
                  <Input
                    type="date"
                    value={editedService?.warranty?.date || ''}
                    onChange={handleChange}
                    name="date"
                  />
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Vto. garantía:
                  </Text>
                  <Input
                    type="date"
                    value={editedService?.warranty?.warrantyExpiration || ''}
                    onChange={handleChange}
                    name="warrantyExpiration"
                  />
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Vehículo Detenido:
                  </Text>
                  <Select
                    value={editedService?.warranty?.stoppedVehicle ?? ''}
                    onChange={handleChange}
                    name="stoppedVehicle"
                  >
                    <option value="">Seleccionar estado</option>
                    <option value={1}>Sí</option>
                    <option value={0}>No</option>
                  </Select>
                </GridItem>
                {/* Segunda fila */}
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Posición AT/AP:
                  </Text>
                  <Input
                    value={editedService?.warranty?.atApPosition || ''}
                    onChange={handleChange}
                    name="atApPosition"
                  />
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Código de Avería:
                  </Text>
                  <Input
                    value={editedService?.warranty?.faultCode || ''}
                    onChange={handleChange}
                    name="faultCode"
                  />
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Código de Fabricante:
                  </Text>
                  <Input
                    value={editedService?.warranty?.manufacturerCode || ''}
                    onChange={handleChange}
                    name="manufacturerCode"
                  />
                </GridItem>
                {/* Tercera fila */}
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Solicitud de Autorización Enviada:
                  </Text>
                  <Input
                    type="date"
                    value={
                      editedService?.warranty?.authorizationRequestSent || ''
                    }
                    onChange={handleChange}
                    name="authorizationRequestSent"
                  />
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Reclamo Enviado:
                  </Text>
                  <Input
                    type="date"
                    value={editedService?.warranty?.claimSent || ''}
                    onChange={handleChange}
                    name="claimSent"
                  />
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    DISS:
                  </Text>
                  <Input
                    value={editedService?.warranty?.diss || ''}
                    onChange={handleChange}
                    name="diss"
                  />
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    TPI:
                  </Text>
                  <Input
                    value={editedService?.warranty?.tpi || ''}
                    onChange={handleChange}
                    name="tpi"
                  />
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Ingresado a SAGA/2:
                  </Text>
                  <Input
                    type="date"
                    value={editedService?.warranty?.saga || ''}
                    onChange={handleChange}
                    name="saga"
                  />
                </GridItem>
                {/* Tercera fila */}
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Nro. Reclamo:
                  </Text>
                  <Input
                    value={editedService?.warranty?.claim || ''}
                    onChange={handleChange}
                    name="claim"
                  />
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    Nro. Autorización:
                  </Text>
                  <Textarea
                    value={editedService?.warranty?.authorization || ''}
                    onChange={handleChange}
                    name="authorization"
                  />
                </GridItem>
              </Grid>
            </Box>
          </Box>

          {/* Tercera área: Piezas */}
          <Box
            mb={6}
            borderWidth="1px"
            borderRadius="xl"
            boxShadow="md"
            bg="white"
          >
            <Box p={6}>
              <Text fontWeight="bold" fontSize="xl" color="gray.700" mb={5}>
                Piezas
              </Text>
              <Button variant="adminPrimary" onClick={handleAddPiece} mb={4}>
                Agregar Pieza
              </Button>
              {editedService?.warranty?.warrantyProducts?.map(
                (product, index) => (
                  <Box key={index} mb={4}>
                    <Grid
                      templateColumns={{
                        base: '1fr',
                        md: '1fr 1fr 1fr 1fr 1fr',
                      }}
                      gap={6}
                    >
                      <GridItem>
                        <FormControl>
                          <FormLabel>Descripción de pieza:</FormLabel>
                          <Input
                            value={product.productDescription}
                            onChange={(e) => handlePieceChange(index, e)}
                            name="productDescription"
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Número de Pieza:</FormLabel>
                          <Input
                            value={product.productNumber}
                            onChange={(e) => handlePieceChange(index, e)}
                            name="productNumber"
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Cantidad:</FormLabel>
                          <NumberInput
                            value={product.quantity}
                            onChange={(value) =>
                              handlePieceChange(index, {
                                target: { name: 'quantity', value },
                              })
                            }
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Nro Operación:</FormLabel>
                          <Input
                            value={product.operationNumber}
                            onChange={(e) => handlePieceChange(index, e)}
                            name="operationNumber"
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Descripción:</FormLabel>
                          <Input
                            value={product.description}
                            onChange={(e) => handlePieceChange(index, e)}
                            name="description"
                          />
                        </FormControl>
                      </GridItem>
                      <GridItem>
                        <FormControl>
                          <FormLabel>Unidad Tiempo:</FormLabel>
                          <Input
                            value={product.timeUnit}
                            onChange={(e) => handlePieceChange(index, e)}
                            name="timeUnit"
                          />
                        </FormControl>
                      </GridItem>
                    </Grid>
                  </Box>
                ),
              )}
            </Box>
          </Box>

          {/* Cuarta área: Argumentos */}
          <Box
            mb={6}
          >
            <Box p={6}>
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
                <GridItem>
                  <FormControl>
                    <Flex
                      align="center"
                      justify="space-between"
                      gap={2}
                      mb={2}
                      flexWrap="wrap"
                    >
                      <FormLabel mb={0}>Argumento Cliente:</FormLabel>
                      <Button
                        size="sm"
                        variant="adminSecondary"
                        onClick={handleAssociateLastReception}
                        isLoading={associatingLastReception}
                      >
                        Asociar a última recepción
                      </Button>
                    </Flex>
                    <Textarea
                      value={editedService?.customerObservations || ''}
                      onChange={handleChange}
                      name="customerObservations"
                      placeholder="Petición del cliente (recepción)"
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl>
                    <FormLabel>Argumento Técnico:</FormLabel>
                    <Textarea
                      value={editedService?.mechanicObservation || ''}
                      onChange={handleChange}
                      name="mechanicObservation"
                      placeholder="Observaciones del técnico"
                    />
                  </FormControl>
                </GridItem>
              </Grid>

              <WarrantyDocumentsPanel
                title="Documentos del diagnóstico (garantía)"
                receptionLabel="Orden de recepción"
                serviceLabel="Informe de servicio"
                warrantyFinalLabel="Orden de garantía"
                canPrintReception={canPrintReception}
                canPrintService={canPrintWarrantyServiceReport}
                canPrintWarrantyFinal={canPrintWarrantyFinal}
                receptionDisabledReason={disabledReasons.warrantyReception}
                serviceDisabledReason={disabledReasons.warrantyService}
                warrantyDisabledReason={disabledReasons.warrantyFinal}
                printingReception={printingPdfKey === `reception-${warrantyServiceId}`}
                printingService={printingPdfKey === `service-${warrantyServiceId}`}
                printingWarranty={printingPdfKey === `warranty-${warrantyServiceId}`}
                onPrintReception={() =>
                  canPrintReception &&
                  regenerateAndOpenPdf(
                    warrantyServiceId,
                    'reception',
                    'Orden de recepción',
                    (docId) =>
                      setWarrantyPdfDocs((prev) => ({
                        ...prev,
                        receptionDocumentId: docId,
                      })),
                  )
                }
                onPrintService={() =>
                  canPrintWarrantyServiceReport &&
                  regenerateAndOpenPdf(
                    warrantyServiceId,
                    'service',
                    'Informe de servicio',
                    (docId) =>
                      setWarrantyPdfDocs((prev) => ({
                        ...prev,
                        serviceDocumentId: docId,
                      })),
                  )
                }
                onPrintWarrantyFinal={() =>
                  canPrintWarrantyFinal &&
                  regenerateAndOpenPdf(
                    warrantyServiceId,
                    'warranty',
                    'Orden de garantía',
                    (docId) =>
                      setWarrantyPdfDocs((prev) => ({
                        ...prev,
                        warrantyDocumentId: docId,
                      })),
                  )
                }
              />
            </Box>
          </Box>
              </TabPanel>

              <TabPanel px={0} pt={4}>
          <Box mb={6}>
            <Box p={6}>
              <FormControl maxW={{ base: '100%', md: '320px' }} mb={5}>
                <FormLabel fontWeight="semibold" color="gray.600" mb={1}>
                  Estado en agenda (reparación)
                </FormLabel>
                <Select
                  size="sm"
                  bg="white"
                  value={repairAgendaStatus}
                  onChange={(e) => setRepairAgendaStatus(e.target.value)}
                  isDisabled={!hasRepairScheduled}
                >
                  {!repairAgendaStatus && (
                    <option value="" disabled>
                      {hasRepairScheduled ? 'Seleccionar estado' : 'Sin reparación agendada'}
                    </option>
                  )}
                  {agendaStatusOptions.map((status) => (
                    <option key={`repair-${status}`} value={status}>
                      {status}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <Text fontWeight="bold" fontSize="xl" color="gray.700" mb={3}>
                Reparación de garantía
              </Text>

            {!vehicleRepairDate && !vehicleRepairTime && !showRepairTime && (
              <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6} mb={4}>
                <GridItem>
                  <Text fontWeight="bold" color="gray.600">
                    No hay reparación agendada
                  </Text>
                </GridItem>
                <GridItem>
                  <Button variant="adminPrimary" onClick={handleShowRepairTime}>
                    Agendar reparación
                  </Button>
                </GridItem>
              </Grid>
            )}

            {vehicleRepairDate && vehicleRepairTime && !showRepairTime && (
              <Box
                p={4}
                borderWidth="1px"
                borderRadius="lg"
                bg="gray.50"
                borderColor="gray.200"
                mb={4}
              >
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }} gap={4}>
                  <GridItem>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">
                      Día
                    </Text>
                    <Text fontSize="md" color="gray.800">
                      {String(vehicleRepairDate).slice(0, 10).split('-').reverse().join('/')}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">
                      Hora
                    </Text>
                    <Text fontSize="md" color="gray.800">
                      {String(vehicleRepairTime).slice(0, 5)}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">
                      Duración
                    </Text>
                    <Text fontSize="md" color="gray.800">
                      {Number(estimatedTime) || 60} min
                    </Text>
                  </GridItem>
                  {repairAgendaStatus && (
                    <GridItem>
                      <Text fontSize="sm" fontWeight="bold" color="gray.600">
                        Estado
                      </Text>
                      <Text fontSize="md" color="gray.800">
                        {repairAgendaStatus}
                      </Text>
                    </GridItem>
                  )}
                </Grid>
                <FormControl mt={4}>
                  <FormLabel fontWeight="bold" color="gray.600" fontSize="sm">
                    Petición del cliente
                  </FormLabel>
                  <Textarea
                    value={newRepairService.customerObservations || ''}
                    onChange={(e) =>
                      setNewRepairService((prev) => ({
                        ...prev,
                        customerObservations: e.target.value,
                      }))
                    }
                    bg="white"
                    rows={3}
                    placeholder="Petición del cliente"
                  />
                </FormControl>
                <Flex gap={2} mt={4} flexWrap="wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleShowRepairTime}
                  >
                    Cambiar horario
                  </Button>
                  {editedService?.warranty?.repairAgendaId &&
                  String(repairAgendaStatus || '').toLowerCase() !== 'cancelado' && (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="red"
                      onClick={handleCancelRepairAppointment}
                      isLoading={cancellingRepair}
                    >
                      Cancelar cita
                    </Button>
                  )}
                </Flex>
              </Box>
            )}

            {showRepairTime && (
              <Box
                p={4}
                borderWidth="1px"
                borderRadius="lg"
                bg="gray.50"
                borderColor="gray.200"
                mb={4}
              >
                <FormControl isRequired maxW={{ base: '100%', md: '240px' }}>
                  <FormLabel fontWeight="bold" color="gray.600">
                    Duración (minutos):
                  </FormLabel>
                  <NumberInput
                    value={estimatedTime}
                    onChange={(valueString) => {
                      const mins = Number(valueString) || 60;
                      setEstimatedTime(mins);
                      setNewRepairService((prev) => ({ ...prev, estimatedTime: mins }));
                    }}
                    min={15}
                    max={480}
                  >
                    <NumberInputField bg="white" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>
                <FormControl mt={4}>
                  <FormLabel fontWeight="bold" color="gray.600">
                    Petición del cliente
                  </FormLabel>
                  <Textarea
                    value={newRepairService.customerObservations || ''}
                    onChange={(e) =>
                      setNewRepairService((prev) => ({
                        ...prev,
                        customerObservations: e.target.value,
                      }))
                    }
                    bg="white"
                    rows={3}
                    placeholder="Petición del cliente"
                  />
                </FormControl>
                <Box
                  mt={4}
                  h="420px"
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="gray.200"
                  overflow="hidden"
                  bg="white"
                >
                  <AvailableTimes
                    selectedDateString={newRepairService.date || vehicleRepairDate || ''}
                    selectedTimeString={newRepairService.time || vehicleRepairTime || ''}
                    duration={estimatedTime}
                    searchByDate={false}
                    show
                    appointmentList={appointmentList}
                    handleServiceChange={handleRepairServiceChange}
                    holidayList={holidayList}
                    selectedMechanicId={selectedMechanicId}
                    disableAutoSelect
                    autoRecommend={false}
                    startOnCurrentWeek={!vehicleRepairDate}
                    recommendTick={recommendTick}
                    allowOccupiedSlot
                    calendarHeight="420px"
                    onAppointmentRangeChange={handleAppointmentRangeChange}
                  />
                </Box>
                <Flex gap={2} mt={4} flexWrap="wrap">
                  <Button variant="adminPrimary" onClick={handleConfirmManualRepairSchedule}>
                    Confirmar reparación
                  </Button>
                  <Button variant="outline" onClick={handleSearchRecommendedSchedule}>
                    Buscar horario recomendado
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (vehicleRepairDate && vehicleRepairTime) {
                        setShowRepairTime(false);
                        return;
                      }
                      setVehicleRepairDate('');
                      setVehicleRepairTime('');
                      setShowRepairTime(false);
                      setNewRepairService((prev) => ({ ...prev, date: '', time: '' }));
                    }}
                  >
                    Cancelar
                  </Button>
                </Flex>
              </Box>
            )}

              <WarrantyDocumentsPanel
                title="Documentos de la reparación"
                receptionLabel="Orden de recepción"
                serviceLabel="Informe de servicio"
                canPrintReception={canPrintRepairReception}
                canPrintService={canPrintRepairServiceReport}
                canPrintWarrantyFinal={false}
                receptionDisabledReason={disabledReasons.repairReception}
                serviceDisabledReason={disabledReasons.repairService}
                printingReception={
                  printingPdfKey === `reception-${repairVehicleServiceId}`
                }
                printingService={
                  printingPdfKey === `service-${repairVehicleServiceId}`
                }
                onPrintReception={() =>
                  canPrintRepairReception &&
                  regenerateAndOpenPdf(
                    repairVehicleServiceId,
                    'reception',
                    'Orden de recepción',
                    (docId) =>
                      setRepairPdfDocs((prev) => ({
                        ...prev,
                        receptionDocumentId: docId,
                      })),
                  )
                }
                onPrintService={() =>
                  canPrintRepairServiceReport &&
                  regenerateAndOpenPdf(
                    repairVehicleServiceId,
                    'service',
                    'Informe de servicio',
                    (docId) =>
                      setRepairPdfDocs((prev) => ({
                        ...prev,
                        serviceDocumentId: docId,
                      })),
                  )
                }
              />
            </Box>
          </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>

          {/* Botón Guardar */}
          <Flex justify="space-between" mt={6} gap={3} flexWrap="wrap">
            <Button onClick={closeModal}>Cancelar</Button>
            <Button
                colorScheme="gray"
                onClick={async () => {
                const warranty = {
                  ...(editedService.warranties || {}),
                  ...(editedService.warranty || {}),
                };
                
                // Preparar datos de la garantía con solo los campos que pertenecen a la entidad
                const guaranteeData = {
                  id: warranty?.id,
                  customerId: warranty?.customerId || editedService.customerId,
                  vehicleId: warranty?.vehicleId || editedService.vehicleId,
                  serviceId: warranty?.serviceId || editedService.id,
                  warrantyType:
                    warranty?.warrantyType ||
                    editedService.warrantyType ||
                    DEFAULT_WARRANTY_TYPE_CODE,
                  status: warranty?.status || DEFAULT_WARRANTY_STATUS,
                  repairAgendaId: warranty?.repairAgendaId || null,
                  date: warranty?.date || null,
                  warrantyExpiration: warranty?.warrantyExpiration || null,
                  stoppedVehicle: warranty?.stoppedVehicle,
                  atApPosition: warranty?.atApPosition || null,
                  faultCode: warranty?.faultCode || null,
                  manufacturerCode: warranty?.manufacturerCode || null,
                  authorizationRequestSent: warranty?.authorizationRequestSent || null,
                  claimSent: warranty?.claimSent || null,
                  diss: warranty?.diss || null,
                  tpi: warranty?.tpi || null,
                  saga: warranty?.saga || null,
                  claim: warranty?.claim || null,
                  authorization: warranty?.authorization || null,
                  warrantyProducts: warranty?.warrantyProducts || [],
                  // Mismos campos del servicio: petición cliente / obs. técnico
                  customerObservations:
                    editedService.customerObservations ?? null,
                  mechanicObservation:
                    editedService.mechanicObservation ?? null,
                };

                console.log('=== FRONTEND WARRANTY DEBUG ===');
                console.log('editedService:', editedService);
                console.log('warranty object:', warranty);
                console.log('guaranteeData to send:', guaranteeData);

                const hasRepairSchedule =
                  Boolean(vehicleRepairDate && vehicleRepairTime);
                const repairAgendaId =
                  warranty?.repairAgendaId ||
                  warranty?.repairAgenda?.id ||
                  null;
                const repairData = hasRepairSchedule
                  ? {
                      ...newRepairService,
                      date: vehicleRepairDate,
                      time: vehicleRepairTime,
                      estimatedTime: Number(estimatedTime) || 60,
                      customerId:
                        newRepairService.customerId ||
                        editedService.customerId,
                      vehicleId:
                        newRepairService.vehicleId ||
                        editedService.vehicleId,
                      mechanicId:
                        newRepairService.mechanicId ||
                        selectedMechanicId ||
                        editedService.mechanicId,
                      serviceTypeId: newRepairService.serviceTypeId || 3,
                      status: repairAgendaStatus || 'Pendiente',
                      observations:
                        newRepairService.observations ||
                        'Reparación de garantía',
                      customerObservations:
                        newRepairService.customerObservations || '',
                      description: 'Reparación de garantía',
                    }
                  : repairAgendaId && repairAgendaStatus
                    ? {
                        statusOnly: true,
                        repairAgendaId,
                        status: repairAgendaStatus,
                      }
                    : null;

                console.log('repairData:', repairData);

                const matchedServiceStatus = statusList.find(
                  (item) => item.name === serviceAgendaStatus,
                );
                const originalServiceStatus =
                  resolveServiceAgendaStatus(selectedService);
                const serviceStatusUpdate =
                  editedService?.id &&
                  serviceAgendaStatus &&
                  serviceAgendaStatus !== originalServiceStatus
                    ? {
                        serviceId: editedService.id,
                        statusId: matchedServiceStatus?.id || null,
                        statusName: serviceAgendaStatus,
                      }
                    : null;

                if (
                  serviceStatusUpdate?.serviceId &&
                  !serviceStatusUpdate?.statusId
                ) {
                  toast({
                    title: 'Estado de agenda no válido',
                    description:
                      'No se pudo identificar el estado seleccionado. Recargá e intentá de nuevo.',
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                  });
                  return;
                }

                await onSave(guaranteeData, repairData, serviceStatusUpdate);
                await loadPdfAvailability();
              }}
            >
              Guardar
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default WarrantyEditModal;
